import React, { useState } from "react";
import { zoneStatusColor, moistureColor, battColor, signalQuality, MiniBar, StatusBadge } from "./irrigationHelpers";

const IrrZoneControlPanel = ({ d }) => {
  const [selected, setSelected] = useState(d.zones[0].id);
  const [filterCrop, setFilterCrop] = useState("All");

  const crops = ["All", ...new Set(d.zones.map(z => z.crop))];
  const filtered = filterCrop === "All" ? d.zones : d.zones.filter(z => z.crop === filterCrop);
  const zone = d.zones.find(z => z.id === selected) || d.zones[0];
  const zst = zoneStatusColor(zone.status);
  const sq  = signalQuality(zone.rssi);

  return (
    <div className="irr-zone-layout">

      {/* ── Left: Zone List ── */}
      <div className="irr-zone-list-panel">
        <div className="irr-zl-filter">
          {crops.map(c => (
            <button key={c} className={`irr-filter-chip ${filterCrop === c ? "irr-filter-active" : ""}`} onClick={() => setFilterCrop(c)}>{c}</button>
          ))}
        </div>
        {filtered.map(z => {
          const st = zoneStatusColor(z.status);
          return (
            <div key={z.id} className={`irr-zone-item ${z.id === selected ? "irr-zone-item-sel" : ""}`} onClick={() => setSelected(z.id)}>
              <div className="irr-zi-top">
                <div>
                  <div className="irr-zi-id">{z.id}</div>
                  <div className="irr-zi-name">{z.name}</div>
                  <div className="irr-zi-crop">{z.crop} · {z.area} ha</div>
                </div>
                <span className="irr-badge" style={{ background: st.bg, color: st.color, fontSize: 11 }}>{st.label}</span>
              </div>
              <div className="irr-zi-bars">
                <div className="irr-zi-bar-row">
                  <span>Moisture</span>
                  <MiniBar value={z.soilMoisture} max={100} color={moistureColor(z.soilMoisture)} />
                  <span>{z.soilMoisture}%</span>
                </div>
                <div className="irr-zi-bar-row">
                  <span>Battery</span>
                  <MiniBar value={z.nodeBattery} max={100} color={battColor(z.nodeBattery)} />
                  <span>{z.nodeBattery}%</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Right: Detail & Controls ── */}
      <div className="irr-zone-detail-panel">
        <div className="irr-zd-header" style={{ borderColor: zst.color }}>
          <div>
            <div className="irr-zd-id">{zone.id} — {zone.name}</div>
            <div className="irr-zd-crop">Crop: {zone.crop} · {zone.area} ha · Mode: {zone.mode}</div>
          </div>
          <StatusBadge status={zone.status} />
        </div>

        {/* Sensor data */}
        <div className="irr-zd-section-title">Live Sensor Data</div>
        <div className="irr-zd-sensors">
          {[
            { label: "Soil Moisture",    value: `${zone.soilMoisture}%`,     sub: `Target: ${zone.soilMoistureTarget}%`,   color: moistureColor(zone.soilMoisture) },
            { label: "Pipeline Pressure",value: `${zone.pressure} bar`,       sub: zone.pressure < 2 ? "⚠ Low pressure!" : "Normal range",  color: zone.pressure < 2 ? "#ef4444" : "#10b981" },
            { label: "Flow Rate",        value: zone.flowRate > 0 ? `${zone.flowRate} L/min` : "Closed", sub: zone.flowRate > 0 ? "Active" : "Valve closed", color: zone.flowRate > 0 ? "#3b82f6" : "#6b7280" },
            { label: "Valve Status",     value: zone.valveStatus,              sub: "",                                     color: zone.valveStatus === "Open" ? "#10b981" : zone.valveStatus === "Closed" ? "#6b7280" : "#ef4444" },
            { label: "Water Delivered",  value: `${zone.waterVolume} L`,       sub: "Today total",                          color: "#3b82f6" },
            { label: "Run Duration",     value: `${zone.duration} min`,        sub: "Today total",                          color: "#8b5cf6" },
            { label: "Node Battery",     value: `${zone.nodeBattery}%`,        sub: zone.nodeId,                            color: battColor(zone.nodeBattery) },
            { label: "LoRa RSSI",        value: `${zone.rssi} dBm`,            sub: sq.label,                               color: sq.color },
            { label: "LoRa SNR",         value: `${zone.snr} dB`,              sub: "Signal quality",                       color: "#667eea" },
          ].map(s => (
            <div key={s.label} className="irr-zd-sensor-card" style={{ borderTopColor: s.color }}>
              <div className="irr-zd-s-val" style={{ color: s.color }}>{s.value}</div>
              <div className="irr-zd-s-lbl">{s.label}</div>
              {s.sub && <div className="irr-zd-s-sub">{s.sub}</div>}
            </div>
          ))}
        </div>

        {/* Moisture bar */}
        <div className="irr-moisture-bar-row">
          <span>Soil Moisture</span>
          <div style={{ flex: 1, background: "#f3f4f6", borderRadius: 6, height: 10, overflow: "hidden" }}>
            <div style={{ width: `${zone.soilMoisture}%`, background: moistureColor(zone.soilMoisture), height: "100%", borderRadius: 6 }} />
          </div>
          <span>{zone.soilMoisture}% / {zone.soilMoistureTarget}% target</span>
        </div>

        {/* Controls */}
        <div className="irr-zd-section-title" style={{ marginTop: 16 }}>Zone Controls</div>
        <div className="irr-zd-controls">
          <div className="irr-ctrl-row">
            <button className="irr-ctrl-btn irr-ctrl-green">▶ Start Irrigation</button>
            <button className="irr-ctrl-btn irr-ctrl-red">⏹ Stop Irrigation</button>
            <button className="irr-ctrl-btn irr-ctrl-blue">🔓 Open Valve</button>
            <button className="irr-ctrl-btn irr-ctrl-gray">🔒 Close Valve</button>
          </div>
          <div className="irr-ctrl-mode">
            <span className="irr-ctrl-mode-lbl">Mode:</span>
            <button className={`irr-mode-btn ${zone.mode === "Auto" ? "irr-mode-active" : ""}`}>Auto</button>
            <button className={`irr-mode-btn ${zone.mode === "Manual" ? "irr-mode-active" : ""}`}>Manual</button>
          </div>
          <div className="irr-ctrl-inputs">
            <label className="irr-ctrl-label">Set Duration (min)
              <input type="number" defaultValue={30} min={1} max={180} className="irr-ctrl-input" />
            </label>
            <label className="irr-ctrl-label">Target Moisture %
              <input type="number" defaultValue={zone.soilMoistureTarget} min={10} max={80} className="irr-ctrl-input" />
            </label>
            <button className="irr-ctrl-btn irr-ctrl-purple" style={{ alignSelf: "flex-end" }}>Apply Settings</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IrrZoneControlPanel;
